import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { motion } from "framer-motion";
import Breadcrumbs from "../../component/breadcrumbs/breadcrumbs";
import "./pricing.css";
import { useLanguage } from "../../i18n/LanguageContext";

export default function Pricing() {
  const navigate = useNavigate();
  const { tr } = useLanguage();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const plans = [
    {
      name: tr("Prototype", "Prototype"),
      price: "$999",
      description: tr(
        "For teams who want to test and validate an AI idea quickly",
        "Untuk tim yang ingin menguji dan memvalidasi ide AI dengan cepat"
      ),
      features: [
        tr("1 core workflow (e.g. chatbot, automation)", "1 core workflow (contoh: chatbot, automation)"),
        tr("1 tool/integration (e.g. Notion, WhatsApp)", "1 tool/integrasi (contoh: Notion, WhatsApp)"),
        tr("Basic UI or no-code setup", "Basic UI atau setup no-code"),
        tr("1 revision round", "1 kali revisi"),
        tr("14–28 working days delivery", "Estimasi 14–28 hari kerja"),
        tr("Option to upgrade to full build", "Opsi upgrade ke full build"),
      ],
      cta: tr("Get Started", "Mulai"),
      highlighted: true,
    },
    {
      name: (
        <>
          {tr("Custom AI Solution (Enterprise)", "Solusi AI Kustom (Enterprise)")}
        </>
      ),
      price: tr("Custom Pricing", "Harga Kustom"),
      description: tr(
        "For businesses with complex needs, long-term support, or scaling requirements",
        "Untuk bisnis dengan kebutuhan kompleks, dukungan jangka panjang, atau kebutuhan scaling"
      ),
      features: [
        tr("Fully custom workflows and logic", "Workflow & logic sepenuhnya kustom"),
        tr("Multi-platform and multi-user support", "Dukungan multi platform dan multi user"),
        tr("Dedicated AI + dev team", "Tim AI + dev dedicated"),
        tr("Long-term support or retainer options", "Opsi support jangka panjang/retainer"),
        tr("Post-deployment optimization", "Optimasi pasca-deployment"),
        tr("Quote after scoping session", "Quote setelah sesi scoping"),
      ],
      cta: tr("Request Quote", "Minta Penawaran"),
      highlighted: false,
    },
  ];

  const handleCtaClick = () => {
    // Navigate to home and jump to the form section
    navigate("/#contact-us");
  };

  return (
    <section className="pricing-section">
      <Breadcrumbs />
      {/* Heading */}
      <motion.div
        className="pricing-heading"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: isMobile ? 0.3 : 0.5 }}
      >
        <h1 className="pricing-title">
        {tr("Packages & Pricing", "Paket & Harga")}
        </h1>

        <p className="pricing-subtitle">
        {tr(
          "Choose AI solution that fits your workflow and budget - from prototype to full build",
          "Pilih solusi AI yang sesuai workflow dan budget - dari prototype hingga full build"
        )}
        </p>

        {/* Toggle removed: one-time pricing */}
      </motion.div>

      {/* Cards */}
      <div className="pricing-grid">
        {plans.map((plan, i) => (
          <motion.div
            key={plan.name}
            initial={{ opacity: 0, y: 30 }}
            {...(isMobile 
              ? { animate: { opacity: 1, y: 0 }, transition: { delay: i * 0.05, duration: 0.3 }, whileHover: { scale: 1.03 } }
              : { whileInView: { opacity: 1, y: 0 }, transition: { delay: i * 0.15, duration: 0.5 }, viewport: { once: true, margin: "-50px" }, whileHover: { scale: 1.03 } }
            )}
            className={`pricing-card ${plan.highlighted ? "pricing-card-highlighted" : ""}`}
          >
            {/* Badge */}
            {plan.highlighted && (
              <motion.div
                className="pricing-badge"
                initial={{ opacity: 0, y: -10 }}
                {...(isMobile 
                  ? { animate: { opacity: 1, y: 0 }, transition: { delay: i * 0.05 + 0.1, duration: 0.3 } }
                  : { whileInView: { opacity: 1, y: 0 }, transition: { delay: 0.1, duration: 0.4 }, viewport: { once: true, margin: "-50px" } }
                )}
              >
                <span className="pricing-badge-text">
                  {tr("• Most Popular", "• Paling Populer")}
                </span>
              </motion.div>
            )}

            {/* Title */}
            <h3 className="pricing-card-title">{plan.name}</h3>

            {/* Price */}
            <div className="pricing-price">
              <span className="pricing-price-amount">
                {plan.price}
              </span>
            {plan.priceNote && (
              <span className="pricing-price-period">{plan.priceNote}</span>
              )}
            </div>

            <p className="pricing-card-description">{plan.description}</p>

            {/* Features */}
            <div className="pricing-features">
              {plan.features.map((f) => (
                <div key={f} className="pricing-feature-item">
                  <Check className="pricing-check-icon" />
                  <span className="pricing-feature-text">{f}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              className={`pricing-cta ${plan.highlighted ? "pricing-cta-highlighted" : "pricing-cta-default"}`}
              onClick={handleCtaClick}
            >
              {plan.cta}
            </motion.button>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

